import { useState, useCallback, useEffect, useRef } from 'react';

export interface TurnstileState {
  token: string | null;
  isVerified: boolean;
  isLoading: boolean;
  error: string | null;
}

// Timeout for Turnstile verification (3 seconds - fail fast, proceed anyway)
const TURNSTILE_TIMEOUT_MS = 3000;

export function useTurnstile() {
  const [state, setState] = useState<TurnstileState>({
    token: null,
    isVerified: false,
    isLoading: true,
    error: null,
  });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Timeout to prevent blocking forever
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      console.warn('Turnstile timeout - proceeding without verification');
      setState((prev) => {
        if (prev.isLoading && !prev.isVerified) {
          return {
            token: null,
            isVerified: true, // Allow to proceed
            isLoading: false,
            error: null, // Don't show error, just proceed
          };
        }
        return prev;
      });
    }, TURNSTILE_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const onSuccess = useCallback((token: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setState({
      token,
      isVerified: true,
      isLoading: false,
      error: null,
    });
  }, []);

  const onError = useCallback(() => {
    setState(prev => ({
      ...prev,
      isLoading: false,
      error: 'Verification failed. Please refresh the page.',
    }));
  }, []);

  const onExpire = useCallback(() => {
    setState(prev => ({
      ...prev,
      token: null,
      isVerified: false,
      error: 'Verification expired. Please refresh.',
    }));
  }, []);

  return {
    ...state,
    onSuccess,
    onError,
    onExpire,
  };
}
